import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { PolicyModel, PolicyToAdd, PolicyType } from 'src/app/models/api/governance/policy.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { PolicyTypeSelectOptions } from 'src/app/models/selectOptions/policyTypeSelectOptions.model';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';

@Component({
  selector: 'app-duplicate-policy-dialog',
  templateUrl: './duplicate-policy-dialog.component.html',
  styleUrls: ['./duplicate-policy-dialog.component.scss'],
})
export class DuplicatePolicyDialogComponent implements OnInit, OnDestroy {
  public duplicatePolicyFormGroup: UntypedFormGroup;

  public policyTypes: ISelectOption[] = PolicyTypeSelectOptions.filter((type) => type.value !== PolicyType.default);

  public waitingForResponse = false;

  private _policyTypeSelected: ISelectOption | undefined;
  private _addPolicySubscription$: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public policyModel: PolicyModel,
    public dialogRef: MatDialogRef<DuplicatePolicyDialogComponent>,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService
  ) {}

  ngOnInit(): void {
    this.duplicatePolicyFormGroup = this._formBuilder.group({
      policyName: this._formBuilder.control('Copy of - ' + this.policyModel.name, [Validators.required]),
      policyType: this._formBuilder.control(this._policyTypeSelected?.displayName, [Validators.required]),
    });
  }

  ngOnDestroy() {
    this._addPolicySubscription$.unsubscribe();
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public onPolicyTypeSelection(selectedPolicyType: ISelectOption) {
    this._policyTypeSelected = this.policyTypes.find((type) => type.id === selectedPolicyType.id);
    this.duplicatePolicyFormGroup.controls['policyType'].setValue(this._policyTypeSelected?.displayName);
  }

  public duplicatePolicy() {
    this.waitingForResponse = true;
    const policyType: PolicyType =
      PolicyType[this.duplicatePolicyFormGroup.controls['policyType'].value.toLowerCase() as PolicyType];
    this._addPolicySubscription$ = this._policiesHttpService
      .addPolicy(this.policyModel.integrationId, {
        name: this.duplicatePolicyFormGroup.controls['policyName'].value,
        type: policyType,
        fromPolicyId: this.policyModel.id,
        config: this.policyModel.config,
      } as PolicyToAdd)
      .subscribe({
        next: () => {
          this._notificationService.success('Policy added successfully');
          this.dialogRef.close(true);
        },
        error: (httpError: HttpErrorResponse) => {
          if (this._httpErrorHandlerService.isUnhandledExceptionError(httpError.status)) {
            this._httpErrorHandlerService.handle(httpError);
          } else {
            this._notificationService.error(httpError.error.detail);
          }
          this.waitingForResponse = false;
        },
      });
  }
}
